/**
 * Shared helpers for recovering from stale-deploy chunk/asset load failures.
 * Used by main.js (reload on unhandledrejection) and newrelic.ts (onerror + skip reporting).
 */

export const CHUNK_RELOAD_KEY = 'fe-app-chunk-reload-done'

const CHUNK_MESSAGE_REGEX = new RegExp(
  [
    'Failed to fetch dynamically imported module',
    'Importing a module script failed',
    'Unable to preload CSS',
    'error loading dynamically imported module',
    'Failed to load module script',
    // deploy version skew: old chunk imports a renamed export from a new chunk
    'provide an export named'
  ].join('|')
)
const NETWORK_MESSAGE_REGEX = /Failed to fetch|Load failed|NetworkError/
const ASSET_URL_REGEX = /fe-app-assets\//
const URL_SEPARATOR_REGEX = /[\s'")]/
const VUE_ASYNC_COMPONENT_INFO_REGEX = /async component loader/i

/**
 * @param {string} [url] - URL to check
 * @returns {boolean}
 */
export function isAssetUrl(url) {
  return typeof url === 'string' && ASSET_URL_REGEX.test(url)
}

/**
 * @param {unknown} reason - Rejection reason or error object
 * @returns {string}
 */
function getErrorMessage(reason) {
  if (typeof reason === 'string') return reason
  if (reason?.message != null) return String(reason.message)
  if (reason?.description != null) return String(reason.description)
  return ''
}

/**
 * @param {unknown} reason - Rejection reason or error object
 * @param {string} [source] - Optional URL (e.g. from window.onerror)
 * @param {string} [message] - Optional message to scan for an embedded asset URL
 * @returns {string}
 */
function getErrorUrl(reason, source, message = '') {
  let url = source || ''
  if (!url && reason?.target?.href) url = reason.target.href
  if (!url && reason?.url != null) url = String(reason.url)
  if (!url && reason?.stack != null) url = String(reason.stack)
  if (!url) url = getAssetUrlFromMessage(message)
  return url
}

/**
 * @param {string} message - Error message to scan
 * @returns {string}
 */
function getAssetUrlFromMessage(message) {
  const assetPathIndex = message.indexOf('fe-app-assets/')
  if (assetPathIndex === -1) return ''

  const absoluteUrlIndex = message.lastIndexOf('http', assetPathIndex)
  const relativeUrlIndex = message.lastIndexOf('/', assetPathIndex)
  const startIndex = absoluteUrlIndex >= 0 ? absoluteUrlIndex : relativeUrlIndex

  if (startIndex === -1) return ''

  const separatorMatch = URL_SEPARATOR_REGEX.exec(message.slice(assetPathIndex))
  const endIndex =
    separatorMatch === null
      ? message.length
      : assetPathIndex + separatorMatch.index

  return message.slice(startIndex, endIndex)
}

/**
 * @param {string} msg - Error message
 * @param {string} [info] - Vue error handler info
 * @returns {boolean}
 */
function isVueAsyncComponentNetworkFailure(msg, info) {
  return (
    typeof info === 'string' &&
    VUE_ASYNC_COMPONENT_INFO_REGEX.test(info) &&
    NETWORK_MESSAGE_REGEX.test(msg)
  )
}

/**
 * @param {unknown} reason - Rejection reason or error object
 * @param {string} [source] - Optional URL (e.g. from window.onerror)
 * @param {string} [info] - Optional Vue error handler info
 * @returns {boolean}
 */
export function isChunkOrAssetFailure(reason, source, info) {
  const msg = getErrorMessage(reason)
  const url = getErrorUrl(reason, source, msg)

  const hasAssetUrl = isAssetUrl(url)
  const isChunkMessage = CHUNK_MESSAGE_REGEX.test(msg)
  const isNetworkFailureOnAsset = NETWORK_MESSAGE_REGEX.test(msg) && hasAssetUrl
  return (
    isChunkMessage ||
    isNetworkFailureOnAsset ||
    isVueAsyncComponentNetworkFailure(msg, info)
  )
}

/**
 * Reloads the page once per session when called for chunk/asset errors.
 * Safe to call multiple times: after the first reload (or when the key is already set), it no-ops.
 * @returns {boolean} True when a reload was triggered.
 */
export function tryReloadForChunkError() {
  if (globalThis.window == undefined) return false
  if (globalThis.sessionStorage.getItem(CHUNK_RELOAD_KEY)) return false
  globalThis.sessionStorage.setItem(CHUNK_RELOAD_KEY, '1')
  globalThis.window.location.reload()
  return true
}
